import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import { API_BASE_URL, SUPPORT_EMAIL } from '../config/api';
import { useApp } from '../context/AppContext';
import useExaminerVisibility from '../utils/useExaminerVisibility';

const UserManual = () => {
  const showExaminer = useExaminerVisibility();
  const [activeSection, setActiveSection] = useState('overview');
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportForm, setSupportForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [supportMessage, setSupportMessage] = useState('');
  const { currentUser } = useApp();

  const sectionStyle = {
    marginBottom: '3rem',
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #dee2e6'
  };

  const stepStyle = {
    backgroundColor: '#e3f2fd',
    padding: '1rem',
    margin: '0.5rem 0',
    borderRadius: '6px',
    borderLeft: '4px solid #2196f3'
  };

  const warningStyle = {
    backgroundColor: '#fff3cd',
    padding: '1rem',
    margin: '1rem 0',
    borderRadius: '6px',
    borderLeft: '4px solid #ffc107',
    color: '#856404'
  };

  const isClient = currentUser?.user?.orgRole === 'client';
  
  const sections = isClient ? [
    { id: 'overview', title: 'Overview' },
    { id: 'registration', title: 'Registration & Login' },
    { id: 'announcements', title: 'Announcements' },
    { id: 'calendar', title: 'Flight Scheduling' },
    { id: 'events', title: 'Events & Ground School' },
    { id: 'user-menu', title: 'User Menu Features' },
    { id: 'progress', title: 'Training Progress' },
    { id: 'documents', title: 'Documents' }
  ] : [
    { id: 'overview', title: 'Overview' },
    { id: 'registration', title: 'Registration & Login' },
    { id: 'announcements', title: 'Announcements' },
    { id: 'calendar', title: 'Flight Scheduling' },
    { id: 'maneuvers', title: 'Maneuvers & Training Tasks' },
    { id: 'events', title: 'Events & Ground School' },
    { id: 'user-menu', title: 'User Menu Features' },
    { id: 'lessons', title: 'Lesson Management' },
    { id: 'progress', title: 'Training Progress' },
    { id: 'expenses', title: 'Expense Tracking' },
    { id: 'earnings', title: 'Earnings Statements' },
    { id: 'documents', title: 'Documents' },
    { id: 'aircraft', title: 'Aircraft Management' },
    { id: 'people', title: 'People Management' },
    { id: 'training', title: 'Training Programs' },
    { id: 'reports', title: 'Reports & Analytics' },
    { id: 'manage', title: 'Management Tools' },
    { id: 'admin', title: 'Administrative Features' }
  ];

  return (
    <>
      <Navigation />
      <div style={{ display: 'flex', maxWidth: '1200px', margin: '0 auto', padding: '2rem', gap: '2rem' }}>
        {/* Table of Contents */}
        <div style={{ width: '250px', position: 'sticky', top: '2rem', height: 'fit-content' }}>
          <h3 style={{ marginBottom: '1rem', color: '#495057' }}>Table of Contents</h3>
          <nav style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.75rem',
                  margin: '0.25rem 0',
                  backgroundColor: activeSection === section.id ? '#007bff' : 'transparent',
                  color: activeSection === section.id ? 'white' : '#495057',
                  border: 'none',
                  borderRadius: '4px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          <h1 style={{ marginBottom: '2rem', color: '#343a40' }}>Real Air Scheduling - User Manual</h1>
          
          {activeSection === 'registration' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>🔐 Registration & Login</h2>
              <p>Select "Stay signed in" when logging in on a trusted device to keep your session across browser restarts for up to seven days. Leave it unchecked to keep the existing 24-hour session limit. The expiry does not extend when you return. Use Logout from the user menu to clear the saved login immediately. Expired or revoked server sessions still require signing in again.</p>
              
              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>User Types</h3>
              <p>Real Air Scheduling supports two distinct registration paths:</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1rem', backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #90caf9' }}>
                  <h4 style={{ color: '#1976d2', marginBottom: '0.5rem' }}>🏫 Organizations</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Flight instructors, flight schools, and flying clubs who will manage aircraft and students</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#f3e5f5', borderRadius: '6px', border: '1px solid #ce93d8' }}>
                  <h4 style={{ color: '#7b1fa2', marginBottom: '0.5rem' }}>👨‍🎓 Students/Renters</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Individuals who want to book lessons or rent aircraft from existing organizations</p>
                </div>
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Organization Registration</h3>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Visit https://realairadventures.com and click "Register"
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Select "I am a flight instructor, flight school, or flying club"
              </div>
              <div style={stepStyle}>
                <strong>Step 3:</strong> Complete the organization registration form:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Business Name:</strong> Your organization or personal instructor name</li>
                  <li><strong>Contact Information:</strong> Business phone, email, and address</li>
                  <li><strong>Account Details:</strong> Username and secure password</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Step 4:</strong> Confirm your email address via the verification link sent to your inbox
              </div>
              <div style={stepStyle}>
                <strong>Step 5:</strong> Wait for account approval from Real Air Scheduling administrators
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Student/Renter Registration</h3>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Visit https://realairadventures.com and click "Register"
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Select "I am a student or renter looking for a flight school"
              </div>
              <div style={stepStyle}>
                <strong>Step 3:</strong> Search for your flight school or instructor:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li>Enter organization name, city, state, or zip code</li>
                  <li>Select your organization from the search results</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Step 4:</strong> Complete the student registration form:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Personal Information:</strong> Name, phone, email, and address</li>
                  <li><strong>Account Details:</strong> Username and secure password</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Step 5:</strong> Confirm your email and wait for organization approval
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Password Requirements</h3>
              <div style={warningStyle}>
                <strong>🔒 Security Requirements:</strong>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li>Minimum 10 characters</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one lowercase letter</li>
                  <li>At least one number</li>
                  <li>At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
                </ul>
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Login Process</h3>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Enter your username and password
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Click "Login" to access your account
              </div>
              <div style={stepStyle}>
                <strong>Forgot Password:</strong> Use the "Forgot Password" link to reset via email
              </div>

              <div style={warningStyle}>
                <strong>💡 Note:</strong> Independent flight instructors receive FREE access to Real Air Scheduling. Organizations must be approved before students can register under them.
              </div>
            </section>
          )}

          {activeSection === 'announcements' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>📢 Announcements</h2>
              
              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Viewing Announcements</h3>
              <p>Important announcements are displayed at the top of the Flight Schedule page in colored notification boxes.</p>
              
              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Announcement Types</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1rem', backgroundColor: '#d4edda', borderRadius: '6px', border: '1px solid #c3e6cb' }}>
                  <h4 style={{ color: '#155724', marginBottom: '0.5rem' }}>📋 General Updates</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0, color: '#155724' }}>Policy changes, schedule updates</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeaa7' }}>
                  <h4 style={{ color: '#856404', marginBottom: '0.5rem' }}>⚠️ Safety Notices</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0, color: '#856404' }}>Weather alerts, safety reminders</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#f8d7da', borderRadius: '6px', border: '1px solid #f5c6cb' }}>
                  <h4 style={{ color: '#721c24', marginBottom: '0.5rem' }}>🚨 Urgent Alerts</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0, color: '#721c24' }}>Critical information, emergencies</p>
                </div>
              </div>
              
              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Managing Announcements</h3>
              <div style={stepStyle}>
                <strong>Automatic Display:</strong> New announcements appear automatically when you visit the Flight Schedule page
              </div>
              <div style={stepStyle}>
                <strong>Mark as Read:</strong> Click the "×" button on any announcement to mark it as read and dismiss it
              </div>
              <div style={stepStyle}>
                <strong>Unread Tracking:</strong> Only unread announcements are displayed to avoid clutter
              </div>
              
              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Announcement Features</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Color Coding:</strong> Different colors indicate announcement priority and type</li>
                <li><strong>Alternating Backgrounds:</strong> Multiple announcements use alternating light/dark blue backgrounds</li>
                <li><strong>Persistent Display:</strong> Announcements remain visible until manually dismissed</li>
                <li><strong>Organization-wide:</strong> All users in your organization see the same announcements</li>
              </ul>

              <div style={warningStyle}>
                <strong>💡 Tip:</strong> Check the Flight Schedule page regularly for new announcements. Important safety and operational information is communicated through this system.
              </div>
            </section>
          )}

          {activeSection === 'overview' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>📋 Overview</h2>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                Welcome to Real Air Scheduling! This comprehensive flight training management system helps you:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#28a745', marginBottom: '0.5rem' }}>✈️ Schedule Flights</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Book aircraft and instructors for training sessions</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#17a2b8', marginBottom: '0.5rem' }}>📚 Track Progress</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Monitor training requirements and achievements</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#ffc107', marginBottom: '0.5rem' }}>💰 Manage Finances</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Track expenses and earnings statements</p>
                </div>
              </div>
              <div style={warningStyle}>
                <strong>🔐 User Roles:</strong> Your access level determines which features you can use. Contact your administrator if you need additional permissions.
              </div>
            </section>
          )}

          {activeSection === 'calendar' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>📅 Flight Scheduling</h2>
              
              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Calendar Views</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#28a745', marginBottom: '0.5rem' }}>📅 Day View</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Detailed hourly schedule for a single day</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#17a2b8', marginBottom: '0.5rem' }}>📊 Week View</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Seven-day overview with time slots</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#ffc107', marginBottom: '0.5rem' }}>🗓️ Month View</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Monthly calendar with reservation summary</p>
                </div>
              </div>
              
              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Making a Reservation</h3>
              <p>Aircraft scheduled at another location are unavailable here, including 60 minutes of travel time before and after that location's hours. For example, Sunday 08:00-16:00 at location 2 grays out the aircraft at location 1 from 07:00 until 17:00. This applies to the entire booking, including mobile bookings and rescheduling. Instructor schedules retain their 30-minute travel buffer.</p>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Navigate to the "Flight Schedule" page from the main menu
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Select your preferred view (Day/Week/Month) and navigate to desired date
              </div>
              <div style={stepStyle}>
                <strong>Step 3:</strong> Click on available time slot or aircraft column
              </div>
              <div style={stepStyle}>
                <strong>Step 4:</strong> In the reservation dialog, complete the Basic Info tab:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Start/End Date & Time:</strong> Set reservation period</li>
                  <li><strong>Instructor:</strong> Choose from available instructors</li>
                  <li><strong>Aircraft:</strong> Select aircraft (filtered by instructor qualifications)</li>
                  <li><strong>Client:</strong> Search and select student</li>
                  <li><strong>Type:</strong> Flight Training, Discovery, Ground lesson, Rental, or Solo</li>
                  <li><strong>Comments:</strong> Add special notes or requirements</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Step 5:</strong> For Flight Training, use the Maneuvers tab to select specific skills to practice from your training program
              </div>
              <div style={stepStyle}>
                <strong>Step 6:</strong> Click "Create Reservation" to confirm booking
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Safety Pilot Assignment (Rental Reservations)</h3>
              <div style={stepStyle}>
                <strong>When this applies:</strong> Safety Pilot assignment is available for <strong>Rental</strong> reservations.
              </div>
              <div style={stepStyle}>
                <strong>Assigning a safety pilot:</strong> In the reservation dialog, set <strong>Type = Rental</strong>, then choose a user in the <strong>Safety Pilot</strong> field. The list is filtered by availability for the selected reservation time.
              </div>
              <div style={stepStyle}>
                <strong>Charge split:</strong> When a safety pilot is assigned, enter the <strong>Renter charge %</strong>. The <strong>Safety pilot charge %</strong> is calculated automatically as the remaining percentage.
              </div>
              <div style={stepStyle}>
                <strong>Status values:</strong> Safety pilot status can be <strong>Unconfirmed</strong>, <strong>Confirmed</strong>, or <strong>Denied</strong>.
              </div>
              <div style={stepStyle}>
                <strong>Safety pilot permissions:</strong> The assigned safety pilot can open the reservation and only take assignment actions. They cannot edit reservation fields, cancel the reservation, or update lesson/aircraft data from that reservation dialog.
              </div>
              <div style={stepStyle}>
                <strong>Confirm/Deny rules:</strong>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Confirm</strong> button is shown when status is <strong>Unconfirmed</strong> or <strong>Denied</strong></li>
                  <li><strong>Deny</strong> button is shown when status is <strong>Unconfirmed</strong> or <strong>Confirmed</strong></li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Automatic lesson creation on check-in:</strong> When a <strong>Rental</strong> reservation is checked in, the system automatically creates or updates the lesson record for that reservation using the completed aircraft log flight time.
              </div>
              <div style={stepStyle}>
                <strong>Automatic split on confirmed safety pilot:</strong> If the safety pilot is <strong>Confirmed</strong> when the aircraft is checked in, the system creates or updates two lesson records for the same reservation: one for the renter using the renter percentage and one for the safety pilot using the remaining percentage.
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Safety Pilot Email Notifications</h3>
              <div style={stepStyle}>
                <strong>Email on assignment:</strong> When a safety pilot is selected for a rental reservation, the system sends notification email(s) with the reservation details and assignment status.
              </div>
              <div style={stepStyle}>
                <strong>Email on reservation update:</strong> When a reservation that includes a safety pilot is updated, the system sends updated notification email(s) reflecting the latest reservation details.
              </div>
              <div style={warningStyle}>
                <strong>💡 Tip:</strong> If users do not receive these notifications, verify email addresses in user profiles and check spam/junk folders.
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Advanced Features</h3>
              {showExaminer && <><h4>AI Mock Oral Examiner</h4>
              <p>Select Private, Instrument, Commercial or Commercial Multi and the aircraft, provide the required consent, and start. Private is selected initially. There is no student curriculum setup or examiner preference configuration. The server resolves applicable content and examiner settings; unsupported combinations show an explicit availability message. Manage &gt; Training &gt; AI Mock Examiner contains administrator availability and optional usage-limit overrides; server defaults apply without saving overrides. Optional legacy diagnostics and independent content review are under Advanced staff tools, not part of starting an examination.</p>
              <p>Open Training &gt; AI Mock Oral Examiner, select a configured checkride, provide explicit processing/mock-training consent and any required recording consent, then start. Checkrides without organization-managed launch settings cannot start; ask your administrator to finish setup. This is AI mock training, not an FAA DPE, official checkride, endorsement, or evaluation of actual flight skills. Findings are provisional and not instructor-reviewed. Coverage describes only this curriculum's tracked elements, not the full ACS. Existing exams keep their original appearance and voice on reconnect. The fictional character's mouth follows audible speech approximately; idle animation respects reduced-motion settings.</p>
              <p>Your organization's checkride settings determine practice/provisional graded mode and retention. Graded mode requires a saved transcript for assessment evidence; any configured transcript/audio storage requires your explicit recording consent before starting. Changing checkrides clears consent. Microphone permission is checked before reserving an exam. Use mute, playback volume, interrupt and pause controls during voice. Reconnect resumes the same exam without resetting its deadline, including time spent paused.</p>
              <p>Review your history, saved report and pinned references. Consented audio tracks can be downloaded as WAV; these are captured/generated chunks rather than a synchronized recording of exactly what you heard. Delete an examination to withdraw storage consent early; usage is not refunded. Application retention is 30 days, while provider processing and backups have separate policies. Instructors prepare source excerpts and curriculum cases under Instructor authoring / review; a different active organization instructor must approve. Administrators configure reserved-minute limits under Manage &gt; Training &gt; AI Mock Examiner.</p></>}
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Drag & Drop:</strong> Move reservations by dragging to new time slots</li>
                <li><strong>Color Coding:</strong> Different colors for reservation types (Training=green, Rental=yellow, etc.)</li>
                <li><strong>Instructor Availability:</strong> Grayed-out slots show unavailable times</li>
                <li><strong>Aircraft Status:</strong> Real-time availability based on maintenance and usage</li>
                <li><strong>Weather Integration:</strong> Visual indicators for weather conditions</li>
              </ul>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Reservation Tabs (During/After Flight)</h3>
              <div style={stepStyle}>
                <strong>Aircraft Tab:</strong> Check out/in aircraft with Hobbs and Tach readings
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li>Record start/end times and readings</li>
                  <li>On mobile, use the camera button beside a Hobbs or Tach field to scan the counter wheels. Keep the counter inside the single guide with all digits visible and level; the reader locates the counter within the guide to exclude surrounding dial labels. Decimal places are automatic: five total digits means one decimal place, six means two, with four digits before the decimal. A printed decimal point is not required. The reader handles decimal wheels with white backgrounds as well as other text colors.</li>
                  <li>A stable scan fills the field automatically and closes the camera, without a review step. For a rolling rightmost wheel, the reader uses the next digit when recognized, including 9 rolling to 0; a fully visible digit stays unchanged.</li>
                  <li>White-background counters use a separate mechanical-digit reader when ordinary recognition fails. The first scan may show "Preparing mechanical digit reader..." while its model downloads. Keep the scanner open until preparation finishes; images are processed on your device.</li>
                  <li>The counter does not need to touch the guide edges. If the tach needle hides a digit, move the phone sideways to reveal it; moving closer alone will not uncover it. If the visible digits are too small to read, move closer. You can also close the scanner and enter the reading manually. Do not guess a hidden digit.</li>
                  <li>Add flight route and remarks</li>
                  <li>Automatic flight time calculation</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Lesson Tab:</strong> Record training details and billing
                <p style={{ marginTop: '0.5rem' }}><strong>Note:</strong> The Lesson tab also appears for <em>Checkride</em> reservations so examiners and instructors can record checkride-specific details.</p>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Flight Time:</strong> Total flight hours for the lesson</li>
                  <li><strong>Simulator:</strong> Simulator training time</li>
                  <li><strong>Dual Received:</strong> Hours with instructor</li>
                  <li><strong>Ground Instruction:</strong> Pre/post-flight briefing time</li>
                  <li><strong>Debrief:</strong> Lesson summary and instructor feedback</li>
                  <li><strong>Next Flight:</strong> Preparation notes for upcoming lesson</li>
                  <li><strong>Purchases:</strong> Additional items purchased (fuel, materials, etc.)</li>
                  <li><strong>Credit:</strong> Account credit applied to lesson</li>
                  <li><strong>Fee:</strong> Instructor or lesson fee</li>
                  <li><strong>Payment:</strong> Amount paid and payment method</li>
                </ul>
              </div>

              <div style={warningStyle}>
                <strong>⚠️ Important:</strong> Always check instructor availability (green=available, gray=unavailable) and aircraft maintenance status before booking.
              </div>
            </section>
          )}

          {!isClient && activeSection === 'maneuvers' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>✈️ Maneuvers & Training Tasks</h2>
              
              <div style={stepStyle}>
                <strong>Accessing:</strong> Navigate to "Manage" → "Maneuvers" tab (Admin/Superuser only)
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>What are Maneuvers?</h3>
              <p>Maneuvers are specific flight training tasks and skills that students must master during their training. They are organized by training program (Private Pilot, Instrument Rating, Commercial, etc.) and can be selected during flight reservations.</p>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Managing Maneuvers</h3>
              <div style={stepStyle}>
                <strong>Adding New Maneuvers:</strong> Click "Add New" to create a new training task
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Name:</strong> Brief title (e.g., "Steep Turns", "Slow Flight")</li>
                  <li><strong>Description:</strong> Detailed explanation of the maneuver</li>
                  <li><strong>Steps:</strong> Step-by-step instructions (use line breaks for clarity)</li>
                  <li><strong>Training Program:</strong> Associate with specific certificate/rating</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Editing Maneuvers:</strong> Click the edit (✏️) button to modify existing maneuvers
              </div>
              <div style={stepStyle}>
                <strong>Deleting Maneuvers:</strong> Click the delete (🗑️) button to remove unused maneuvers
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Using Maneuvers in Lessons</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>During Booking:</strong> Select maneuvers in the reservation dialog's Maneuvers tab</li>
                <li><strong>Multiple Selection:</strong> Choose multiple maneuvers for a single lesson</li>
                <li><strong>Progress Tracking:</strong> Maneuvers link to student progress tracking</li>
                <li><strong>Lesson Planning:</strong> Helps instructors and students prepare for flights</li>
              </ul>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Display Features</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Formatted Steps:</strong> Line breaks in steps are preserved for readability</li>
                <li><strong>Searchable:</strong> Quickly find maneuvers by name or description</li>
                <li><strong>Organized Table:</strong> Clear columns for Actions, Name, Description, and Steps</li>
              </ul>

              <div style={warningStyle}>
                <strong>💡 Best Practice:</strong> Create comprehensive maneuver libraries for each training program. Include detailed steps to help students prepare and review after lessons.
              </div>
            </section>
          )}

          {activeSection === 'events' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>🎓 Events & Ground School</h2>
              
              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Viewing Events</h3>
              <p>The Events page displays all scheduled ground school sessions, seminars, and training events.</p>
              
              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Registering for Events</h3>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Browse available events on the Events page
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Click "Register" on events you want to attend
              </div>
              <div style={stepStyle}>
                <strong>Step 3:</strong> Confirm your registration details
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Event Types</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Ground School:</strong> Classroom-based training sessions</li>
                <li><strong>Safety Seminars:</strong> Important safety briefings and updates</li>
                <li><strong>Mock Checkrides:</strong> Practice examinations and evaluations</li>
                <li><strong>Social Events:</strong> Community gatherings and networking</li>
              </ul>
            </section>
          )}

          {!isClient && activeSection === 'user-menu' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>👤 User Menu Features</h2>
              
              <p>Click your profile icon in the top-right corner to access personal features:</p>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>My Info</h3>
              <div style={stepStyle}>
                <strong>Basic Info Tab:</strong> Update your contact information, address, and personal details
              </div>
              <div style={stepStyle}>
                <strong>Lessons Tab:</strong> View your complete lesson history with balance information. Click the PDF icon beside Lessons to open a large preview with download and print icons. The PDF includes all your lessons in the active organization, newest first, with complete notes, hours, aircraft/instructor details, and financial fields. Downloads are named lesson-history.pdf. If your browser cannot preview or print the PDF, download it and open it in a PDF viewer.
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>My Schedule (Instructors Only)</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Weekly Schedule:</strong> Set your regular availability for each day</li>
                <li><strong>Schedule Overrides:</strong> Add temporary availability changes (vacations, special hours)</li>
                <li><strong>Time Slots:</strong> Choose from 30-minute increments throughout the day</li>
              </ul>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Change Password</h3>
              <div style={warningStyle}>
                <strong>Password Requirements:</strong>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li>At least 10 characters long</li>
                  <li>One uppercase letter</li>
                  <li>One lowercase letter</li>
                  <li>One number</li>
                  <li>One special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
                </ul>
              </div>
            </section>
          )}

          {!isClient && activeSection === 'lessons' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>📖 Lesson Management</h2>
              
              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>For Students - "My Lessons"</h3>
              <div style={stepStyle}>
                <strong>Accessing:</strong> User Menu → "My Lessons"
              </div>
              <p>View your complete training history including:</p>
              <ul style={{ lineHeight: '1.8', marginBottom: '1.5rem' }}>
                <li><strong>Flight Time:</strong> Total hours flown</li>
                <li><strong>Simulator Time:</strong> Simulator training hours</li>
                <li><strong>Dual Received:</strong> Hours with instructor</li>
                <li><strong>Ground Instruction:</strong> Classroom/briefing time</li>
                <li><strong>Financial Summary:</strong> Purchases, fees, credits, and current balance</li>
                <li><strong>Debrief Notes:</strong> Instructor feedback and lesson summary</li>
                <li><strong>Next Flight Plan:</strong> Preparation for upcoming lessons</li>
              </ul>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>For Instructors - "My Lessons"</h3>
              <div style={stepStyle}>
                <strong>Accessing:</strong> User Menu → "My Lessons"
              </div>
              
              <h4 style={{ color: '#6c757d', marginTop: '1.5rem', marginBottom: '1rem' }}>Recording Lesson Data:</h4>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Click the edit (✏️) button next to any lesson
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Fill in the lesson details:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Date & Time:</strong> When the lesson occurred</li>
                  <li><strong>Client:</strong> Select from active students</li>
                  <li><strong>Dual Given:</strong> Hours of flight instruction provided</li>
                  <li><strong>Ground Instruction:</strong> Pre/post-flight briefing time</li>
                  <li><strong>Total:</strong> Combined instruction time</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Step 3:</strong> Click the save (💾) button to record the lesson
              </div>

              <div style={warningStyle}>
                <strong>💡 Tip:</strong> Your earnings are automatically calculated based on your pay rate and total instruction time.
              </div>
            </section>
          )}

          {!isClient && activeSection === 'progress' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>📈 Training Progress</h2>
              
              <div style={stepStyle}>
                <strong>Accessing:</strong> User Menu → "My Progress"
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Understanding Progress Levels</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1rem', backgroundColor: '#f8d7da', borderRadius: '6px', border: '1px solid #f5c6cb' }}>
                  <h4 style={{ color: '#721c24', marginBottom: '0.5rem' }}>Level 1-2</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0, color: '#721c24' }}>Started to learn, making good progress</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeaa7' }}>
                  <h4 style={{ color: '#856404', marginBottom: '0.5rem' }}>Level 3</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0, color: '#856404' }}>Solo level</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#d4edda', borderRadius: '6px', border: '1px solid #c3e6cb' }}>
                  <h4 style={{ color: '#155724', marginBottom: '0.5rem' }}>Level 4+</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0, color: '#155724' }}>Proficient / Mastered</p>
                </div>
              </div>

              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Progress Categories</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Requirements:</strong> Specific skills or knowledge areas</li>
                <li><strong>Stage:</strong> Training phase (Private, Instrument, Commercial, etc.)</li>
                <li><strong>Status:</strong> Current completion state</li>
                <li><strong>Level:</strong> Proficiency rating from instructor evaluation</li>
              </ul>

              <div style={warningStyle}>
                <strong>📝 Note:</strong> Progress updates are typically made by your instructor during lesson debriefs. Contact your instructor if you have questions about your progress ratings.
              </div>
            </section>
          )}

          {!isClient && activeSection === 'expenses' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>💰 Expense Tracking</h2>
              
              <div style={stepStyle}>
                <strong>Accessing:</strong> User Menu → "My Expenses" (Staff/Instructors only)
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Adding New Expenses</h3>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Click "Add New" button in the expenses dialog
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Fill in expense details:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Date:</strong> When the expense occurred</li>
                  <li><strong>Expense:</strong> Description of the expense</li>
                  <li><strong>Vendor:</strong> Where the purchase was made</li>
                  <li><strong>Total:</strong> Amount spent</li>
                  <li><strong>Receipt:</strong> Upload receipt image (optional)</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Step 3:</strong> Click save (💾) to record the expense
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Managing Receipts</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Upload:</strong> Use the file input when editing an expense</li>
                <li><strong>View:</strong> Click the attachment icon (📎) to view uploaded receipts</li>
                <li><strong>Formats:</strong> Accepts common image formats (JPG, PNG, etc.)</li>
              </ul>

              <div style={warningStyle}>
                <strong>🧾 Best Practice:</strong> Always upload receipts for expense reimbursement. Keep original receipts as backup.
              </div>
            </section>
          )}

          {!isClient && activeSection === 'earnings' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>💵 Earnings Statements</h2>
              
              <div style={stepStyle}>
                <strong>Accessing:</strong> User Menu → "My Earnings Statements" (Staff/Instructors only)
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Viewing Earnings</h3>
              <p>Earnings statements are generated periodically and show:</p>
              <ul style={{ lineHeight: '1.8', marginBottom: '1.5rem' }}>
                <li><strong>Period:</strong> Start and end dates for the statement</li>
                <li><strong>Total Earnings:</strong> Gross pay for instruction time</li>
                <li><strong>Expenses:</strong> Deductible expenses for the period</li>
                <li><strong>Net Amount:</strong> Final payment amount</li>
              </ul>

              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Detailed Breakdown</h3>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Click on any earnings statement row
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Use the tabs to view:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Work Lessons:</strong> All lessons taught during the period</li>
                  <li><strong>Expenses:</strong> All expenses claimed during the period</li>
                </ul>
              </div>

              <div style={warningStyle}>
                <strong>📊 Note:</strong> Earnings are calculated automatically based on your recorded lessons and hourly pay rate. Contact administration for any discrepancies.
              </div>
            </section>
          )}

          {!isClient && activeSection === 'reports' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>📊 Reports & Analytics</h2>
              
              <div style={stepStyle}>
                <strong>Accessing:</strong> Navigate to "Manage" → "Reports" tab (Admin/Superuser only)
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Available Reports</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#007bff', marginBottom: '0.5rem' }}>✈️ Flight Hours</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Aircraft usage, total flights, and average duration</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#28a745', marginBottom: '0.5rem' }}>🛩️ Aircraft Utilization</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Revenue, bookings, and hours flown by aircraft</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#17a2b8', marginBottom: '0.5rem' }}>👨‍🏫 Instructor Activity</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Lessons taught, hours, and student counts</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#ffc107', marginBottom: '0.5rem' }}>💰 Revenue Summary</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Financial performance by category</p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ color: '#6f42c1', marginBottom: '0.5rem' }}>📈 Student Progress</h4>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Training progress and lesson activity</p>
                </div>
              </div>

              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Generating Reports</h3>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Set the date range using Start Date and End Date fields
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Select the report type by clicking on the report tabs
              </div>
              <div style={stepStyle}>
                <strong>Step 3:</strong> Click "Generate Report" to refresh data for the selected dates
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Report Features</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Date Filtering:</strong> Custom date ranges for focused analysis</li>
                <li><strong>Visual Charts:</strong> Progress bars and percentage breakdowns</li>
                <li><strong>Detailed Tables:</strong> Comprehensive data with totals and averages</li>
                <li><strong>Real-time Data:</strong> Up-to-date information from the system</li>
                <li><strong>Multiple Views:</strong> Switch between different report types instantly</li>
              </ul>

              <div style={warningStyle}>
                <strong>📊 Note:</strong> Reports are only available to Admin and Superuser roles. Data reflects all organizational activity within the selected date range.
              </div>
            </section>
          )}

          {!isClient && activeSection === 'documents' && (
            <section style={sectionStyle}>
              <h2 style={{ color: '#007bff', marginBottom: '1.5rem' }}>📄 Documents</h2>
              
              <div style={stepStyle}>
                <strong>Accessing:</strong> User Menu → "My Documents"
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Document Types</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Pilot Certificate:</strong> Private, instrument, commercial, ATP certificates</li>
                <li><strong>Medical Certificate:</strong> FAA medical certificates with expiration tracking</li>
                <li><strong>Driver's License:</strong> Valid driver's license for identification</li>
                <li><strong>Passport:</strong> For international training or travel</li>
                <li><strong>Insurance:</strong> Renter's insurance or personal coverage</li>
                <li><strong>Endorsement:</strong> Instructor endorsements and sign-offs</li>
                <li><strong>Other:</strong> Any additional aviation-related documents</li>
              </ul>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Adding Documents</h3>
              <div style={stepStyle}>
                <strong>Step 1:</strong> Click "Add New" in the documents section
              </div>
              <div style={stepStyle}>
                <strong>Step 2:</strong> Fill in document details:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Document Type:</strong> Select from predefined categories</li>
                  <li><strong>Description:</strong> Brief description or certificate number</li>
                  <li><strong>Document Date:</strong> Issue date</li>
                  <li><strong>Expiration Date:</strong> When document expires (if applicable)</li>
                  <li><strong>Upload:</strong> Scan or photo of the document</li>
                </ul>
              </div>
              <div style={stepStyle}>
                <strong>Step 3:</strong> Click save to store the document
              </div>

              <h3 style={{ color: '#495057', marginTop: '2rem', marginBottom: '1rem' }}>Managing Documents</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>View:</strong> Click the document icon (📄) to view uploaded files</li>
                <li><strong>Edit:</strong> Update document information and upload new versions</li>
                <li><strong>Expiration Tracking:</strong> System tracks expiration dates for compliance</li>
              </ul>

              <div style={warningStyle}>
                <strong>📋 Compliance:</strong> Keep all aviation documents current and uploaded for training eligibility verification.
              </div>
            </section>
          )}

          <div style={{ marginTop: '3rem', padding: '2rem', backgroundColor: '#e9ecef', borderRadius: '8px', textAlign: 'center' }}>
            <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Need Additional Help?</h3>
            <p style={{ marginBottom: '1rem' }}>If you need further assistance or have questions not covered in this manual:</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button onClick={() => setShowSupportForm(true)} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Contact Support</button>
              <button style={{ padding: '0.75rem 1.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Schedule Training</button>
            </div>
          </div>
        </div>
      </div>

      {/* Support Form Modal */}
      {showSupportForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '500px', width: '90%' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Contact Support</h3>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const response = await fetch(`${API_BASE_URL}/support/contact`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'session-id': currentUser?.sessionId
                  },
                  body: JSON.stringify({
                    ...supportForm,
                    supportEmail: SUPPORT_EMAIL,
                    userInfo: currentUser?.user
                  })
                });
                
                if (response.ok) {
                  setSupportMessage('Your message has been sent successfully!');
                  setSupportForm({ name: '', email: '', subject: '', message: '' });
                  setTimeout(() => {
                    setShowSupportForm(false);
                    setSupportMessage('');
                  }, 3000);
                } else {
                  setSupportMessage('Failed to send message. Please try again.');
                }
              } catch (error) {
                setSupportMessage('Error sending message. Please try again.');
              }
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Name:</label>
                <input 
                  type="text" 
                  value={supportForm.name}
                  onChange={(e) => setSupportForm({...supportForm, name: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Email:</label>
                <input 
                  type="email" 
                  value={supportForm.email}
                  onChange={(e) => setSupportForm({...supportForm, email: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Subject:</label>
                <input 
                  type="text" 
                  value={supportForm.subject}
                  onChange={(e) => setSupportForm({...supportForm, subject: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Message:</label>
                <textarea 
                  value={supportForm.message}
                  onChange={(e) => setSupportForm({...supportForm, message: e.target.value})}
                  rows="4"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
                  required 
                />
              </div>
              
              {supportMessage && (
                <div style={{ textAlign: 'center', marginBottom: '1rem', padding: '0.5rem', color: supportMessage.includes('successfully') ? 'green' : 'red', fontWeight: 'bold' }}>
                  {supportMessage}
                </div>
              )}
              
              <div style={{ textAlign: 'center', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button type="submit" style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Send Message</button>
                <button type="button" onClick={() => { setShowSupportForm(false); setSupportMessage(''); }} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManual;